import { useEffect, useState, useContext, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowLeft, FaClipboardList, FaSearch, FaCheckCircle, FaTimesCircle, FaChevronDown, FaChevronUp,
} from "react-icons/fa";
import { AuthContext } from "../../context/AuthContext";

const API = "/api/admin/resultados-encuesta";

export default function ResultadosEncuestaAdmin() {
  const { token } = useContext(AuthContext);

  const [encuestas, setEncuestas] = useState([]);
  const [encuestaId, setEncuestaId] = useState("");
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seccionActiva, setSeccionActiva] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [mostrarPendientes, setMostrarPendientes] = useState(false);

  const cargarEncuestas = useCallback(async () => {
    try {
      const res = await fetch(`${API}/encuestas`, { credentials: "include", headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setEncuestas(data || []);
      if (data && data.length > 0) setEncuestaId(data[0].id);
    } catch (error) {
      console.error("Error cargando encuestas:", error);
    }
  }, [token]);

  useEffect(() => {
    cargarEncuestas();
  }, [cargarEncuestas]);

  useEffect(() => {
    if (!encuestaId) return;
    const cargarResultados = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/encuestas/${encuestaId}/resultados`, {
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setResultado(data);
        setSeccionActiva(data.secciones?.[0]?.id ?? null);
      } catch (error) {
        console.error("Error cargando resultados:", error);
      } finally {
        setLoading(false);
      }
    };
    cargarResultados();
  }, [encuestaId, token]);

  const nombreMiembro = (m) => [m.nombre, m.paterno, m.materno].filter(Boolean).join(" ");

  const seccionActual = resultado?.secciones.find((s) => s.id === seccionActiva);

  const obtenerRespuesta = (idMiembro, pregunta) => {
    const r = resultado?.respuestas.find((r) => r.id_miembro === idMiembro && r.pregunta_id === pregunta.id);
    if (!r) return null;
    if (pregunta.tipo === "si_no") {
      if (r.respuesta_boolean === true) return "Sí";
      if (r.respuesta_boolean === false) return "No";
      return null;
    }
    if (pregunta.tipo === "checkbox") {
      return r.opciones_texto && r.opciones_texto.length > 0 ? r.opciones_texto.join(", ") : null;
    }
    return r.respuesta_texto || null;
  };

  const miembrosSeccion = useMemo(() => {
    if (!resultado || !seccionActual) return [];
    const preguntaIds = new Set(seccionActual.preguntas.map((p) => p.id));
    const idsConRespuesta = new Set(
      resultado.respuestas.filter((r) => preguntaIds.has(r.pregunta_id)).map((r) => r.id_miembro)
    );
    return resultado.miembros
      .filter((m) => idsConRespuesta.has(m.id_miembro))
      .filter((m) => nombreMiembro(m).toLowerCase().includes(busqueda.toLowerCase()));
  }, [resultado, seccionActual, busqueda]);

  const totalActivos = resultado?.miembros.length || 0;
  const totalRespondieron = resultado?.miembros.filter((m) => m.respondido).length || 0;
  const pendientes = resultado?.miembros.filter((m) => !m.respondido) || [];

  return (
    <div className="p-6 bg-slate-50">

      <div className="text-sm text-slate-400 mb-3">
        <Link to="/dashboard/administracion" className="hover:text-indigo-600 flex items-center gap-1 inline-flex">
          <FaArrowLeft className="text-xs" /> Administración
        </Link>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Resultados de Encuesta</h1>
        <p className="text-sm text-slate-500">
          Consulta las respuestas por sección y miembro para apoyar la toma de decisiones.
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Encuesta</label>
          <select
            value={encuestaId}
            onChange={(e) => setEncuestaId(Number(e.target.value))}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white min-w-[220px] focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          >
            {encuestas.length === 0 && <option value="">Sin encuestas</option>}
            {encuestas.map((e) => (
              <option key={e.id} value={e.id}>{e.nombre} ({e.anio})</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-10 text-center text-sm text-slate-400">
          Cargando resultados...
        </div>
      ) : !resultado ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-10 text-center text-sm text-slate-400">
          Selecciona una encuesta para ver sus resultados.
        </div>
      ) : (
        <>
          {/* RESUMEN DE PARTICIPACIÓN */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex items-center gap-3">
              <span className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                <FaCheckCircle />
              </span>
              <div>
                <p className="text-xs text-slate-500">Respondieron</p>
                <p className="text-xl font-bold text-slate-800">{totalRespondieron} <span className="text-sm font-normal text-slate-400">/ {totalActivos}</span></p>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex items-center gap-3">
              <span className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                <FaTimesCircle />
              </span>
              <div>
                <p className="text-xs text-slate-500">Pendientes</p>
                <p className="text-xl font-bold text-slate-800">{pendientes.length}</p>
              </div>
            </div>
            <button
              onClick={() => setMostrarPendientes(!mostrarPendientes)}
              className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex items-center justify-between gap-3 hover:border-indigo-300 transition text-left"
            >
              <span className="text-sm font-medium text-slate-600">Ver miembros pendientes</span>
              {mostrarPendientes ? <FaChevronUp className="text-slate-400 text-xs" /> : <FaChevronDown className="text-slate-400 text-xs" />}
            </button>
          </div>

          {mostrarPendientes && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4">
              {pendientes.length === 0 ? (
                <p className="text-sm text-slate-400">Todos los miembros activos han respondido.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {pendientes.map((m) => (
                    <span key={m.id_miembro} className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-3 py-1">
                      {nombreMiembro(m)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TABS DE SECCIÓN */}
          <div className="flex flex-wrap gap-2 mb-4">
            {resultado.secciones.map((s) => (
              <button
                key={s.id}
                onClick={() => setSeccionActiva(s.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${
                  seccionActiva === s.id
                    ? "bg-indigo-600 border-indigo-600 text-white"
                    : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                {s.nombre}
              </button>
            ))}
          </div>

          {/* TABLA DE RESULTADOS DE LA SECCIÓN */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200">
            <div className="p-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                  <FaClipboardList className="text-xs" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-slate-800">{seccionActual?.nombre}</p>
                  <p className="text-xs text-slate-400">{miembrosSeccion.length} miembros respondieron en esta sección</p>
                </div>
              </div>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-sm" />
                <input
                  type="text"
                  placeholder="Buscar miembro..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  className="pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold sticky left-0 bg-slate-50">Miembro</th>
                    {seccionActual?.preguntas.map((p) => (
                      <th key={p.id} className="px-4 py-3 text-left font-semibold min-w-[180px]">{p.texto}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {miembrosSeccion.length === 0 ? (
                    <tr>
                      <td colSpan={(seccionActual?.preguntas.length || 0) + 1} className="text-center py-10 text-slate-400">
                        Ningún miembro ha respondido esta sección todavía.
                      </td>
                    </tr>
                  ) : (
                    miembrosSeccion.map((m) => (
                      <tr key={m.id_miembro} className="border-t border-slate-100 hover:bg-slate-50 transition">
                        <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap sticky left-0 bg-white">
                          {nombreMiembro(m)}
                        </td>
                        {seccionActual?.preguntas.map((p) => {
                          const valor = obtenerRespuesta(m.id_miembro, p);
                          return (
                            <td key={p.id} className="px-4 py-3 text-slate-600">
                              {valor === null ? (
                                <span className="text-slate-300">—</span>
                              ) : p.tipo === "si_no" ? (
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                  valor === "Sí" ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"
                                }`}>
                                  {valor}
                                </span>
                              ) : (
                                valor
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

    </div>
  );
}
